import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { User } from "@/lib/users.api";
import { humanizeAuditAction } from "../auditLogPage.helpers";
import type { AuditLogPageFilters } from "../auditLogPage.types";

interface AuditLogToolbarProps {
  filters: AuditLogPageFilters;
  actions: string[];
  entityTypes: string[];
  userOptions: User[];
  isFetching: boolean;
  onFiltersChange: (filters: Partial<AuditLogPageFilters>) => void;
  onResetFilters: () => void;
}

export function AuditLogToolbar({
  filters,
  actions,
  entityTypes,
  userOptions,
  isFetching,
  onFiltersChange,
  onResetFilters,
}: AuditLogToolbarProps) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Select
        value={filters.userId !== undefined ? String(filters.userId) : "__all__"}
        onValueChange={(value) =>
          onFiltersChange({ userId: value === "__all__" ? undefined : parseInt(value) })
        }
      >
        <SelectTrigger className="w-44">
          <SelectValue placeholder="Tous les utilisateurs" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tous les utilisateurs</SelectItem>
          {userOptions.map((user) => (
            <SelectItem key={user.id} value={String(user.id)}>
              {user.fullName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.action ?? "__all__"}
        onValueChange={(value) =>
          onFiltersChange({ action: value === "__all__" ? undefined : value })
        }
      >
        <SelectTrigger className="w-52">
          <SelectValue placeholder="Toutes les actions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Toutes les actions</SelectItem>
          {actions.map((action) => (
            <SelectItem key={action} value={action}>
              {humanizeAuditAction(action)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.entityType ?? "__all__"}
        onValueChange={(value) =>
          onFiltersChange({ entityType: value === "__all__" ? undefined : value })
        }
      >
        <SelectTrigger className="w-44">
          <SelectValue placeholder="Toutes les entités" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Toutes les entités</SelectItem>
          {entityTypes.map((entityType) => (
            <SelectItem key={entityType} value={entityType}>
              {entityType}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DatePicker
        value={filters.from}
        onChange={(value) => onFiltersChange({ from: value })}
        placeholder="Depuis"
        className="w-40"
      />
      <DatePicker
        value={filters.to}
        onChange={(value) => onFiltersChange({ to: value })}
        placeholder="Jusqu'à"
        className="w-40"
      />

      <Button variant="secondary" size="sm" onClick={onResetFilters}>
        Réinitialiser
      </Button>

      {isFetching && (
        <span className="flex items-center gap-1.5 text-[11px] text-subtle">
          <Loader2 size={12} className="animate-spin" />
          Recherche...
        </span>
      )}
    </div>
  );
}
